import React, { useEffect, useState, useRef, useCallback } from "react";
import { MapContainer, TileLayer, GeoJSON, LayersControl } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import proj4 from "proj4";
import { FaCircleInfo } from "react-icons/fa6";
import { MapHomeControl, MapLegendControl } from "../../../../common/MapControls/MapControls";

// Initial map view state
const INITIAL_VIEW_STATE = {
  longitude: 125.7275,
  latitude: -8.8742,
  zoom: 8,
};
const InfoButton = ({ onClick }) => {
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  return (
    <div
      ref={btnRef}
      style={{
        position: "absolute",
        bottom: "20px",
        left: "12px",
        padding: "8px",
        borderRadius: "8px",
        fontSize: "15px",
        //boxShadow: "0 0 6px rgba(0,0,0,0.3)",
        cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
        zIndex: 1000,
      }}
      onClick={onClick}
    >
      <FaCircleInfo />
    </div>
  );
};

// Define UTM Zone 51S projection and WGS84 projection
const UTM_51S = "+proj=utm +zone=51 +south +datum=WGS84 +units=m +no_defs";
const WGS84 = "EPSG:4326";

// Function to reproject a GeoJSON object
const reprojectGeoJSON = (geojson) => {
  return {
    ...geojson,
    features: geojson.features
      .filter((feature) => feature.geometry) // Filter out features with null geometry
      .map((feature) => {
        const { geometry } = feature;

        if (geometry.type === "Point") {
          const [x, y] = geometry.coordinates;
          const [lon, lat] = proj4(UTM_51S, WGS84, [x, y]);
          return {
            ...feature,
            geometry: { ...geometry, coordinates: [lon, lat] },
          };
        } else if (geometry.type === "LineString") {
          const reprojectedCoords = geometry.coordinates.map(([x, y]) =>
            proj4(UTM_51S, WGS84, [x, y])
          );
          return {
            ...feature,
            geometry: { ...geometry, coordinates: reprojectedCoords },
          };
        } else if (geometry.type === "Polygon") {
          const reprojectedCoords = geometry.coordinates.map((ring) =>
            ring.map(([x, y]) => proj4(UTM_51S, WGS84, [x, y]))
          );
          return {
            ...feature,
            geometry: { ...geometry, coordinates: reprojectedCoords },
          };
        } else {
          console.warn(`Geometry type "${geometry.type}" is not supported.`);
          return feature;
        }
      }),
  };
};

const HydrologicalData = () => {
  const [boundaryData, setBoundaryData] = useState(null);
  const [lineData, setLineData] = useState(null);
  const [pointData, setPointData] = useState(null);
  const [polygonData, setPolygonData] = useState(null);
  const [legendVisible, setLegendVisible] = useState(false);
  const legendRef = useRef(null);
      const [info, setInfo] = useState(false);
      const infoRef = useRef(null);
      const infoPanelRef = useCallback((node) => {
        infoRef.current = node;
        if (node) {
          L.DomEvent.disableClickPropagation(node);
          L.DomEvent.disableScrollPropagation(node);
        }
      }, []);

  // Load GeoJSON data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Boundary data
        const boundaryResponse = await fetch(
          "/DATA/OSMbased_adm0_boundary.json"
        );
        const boundaryJson = await boundaryResponse.json();
        setBoundaryData(boundaryJson);

        // Fetch and reproject Water Points
        const pointResponse = await fetch(
          "/DATA/Landslide/OSM_MICR_BING_point_filt.json"
        );
        const pointJson = await pointResponse.json();
        setPointData(reprojectGeoJSON(pointJson));

        // Fetch and reproject Water Drain Lines
        const lineResponse = await fetch(
          "/DATA/Landslide/OSM_MICR_BING_line_filt.json"
        );
        const lineJson = await lineResponse.json();
        setLineData(reprojectGeoJSON(lineJson));

        // Fetch and reproject Polygons
        const polygonResponse = await fetch(
          "/DATA/Landslide/OSM_MICR_BING_poly_filt.json"
        );
        const polygonJson = await polygonResponse.json();
        setPolygonData(reprojectGeoJSON(polygonJson));
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };

    fetchData();
  }, []);


  // Close legend when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (legendRef.current && !legendRef.current.contains(event.target)) {
        setLegendVisible(false);
      }
    };

    if (legendVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [legendVisible]);

  // Function to bind popups to features
  const bindPopups = (feature, layer) => {
    if (feature.properties) {
      const popupContent = Object.entries(feature.properties)
        .map(([key, value]) => `<b>${key}</b>: ${value}`)
        .join("<br>");
      layer.bindPopup(popupContent);
    }
  };

  return (
    <div className="wind-speed-cyclone-container">
      <div className="map-container-WindSpeedCyclone">
        <MapContainer
          center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
          zoom={INITIAL_VIEW_STATE.zoom}
          className="mapcontainerall"
        >
          <MapHomeControl />
                              {info && (
            <div
              ref={infoPanelRef}
              style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
            >
              The Water drains, Water Points, Buildings information are available here.
            </div>
          )}
          <InfoButton onClick={() => setInfo(!info)} />
          {/* Base Layers */}
          <LayersControl position="topright">
            <LayersControl.BaseLayer name="White Theme" checked>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="OpenStreetMap">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
              />
            </LayersControl.BaseLayer>

            {/* Boundary Layer */}
            {boundaryData && (
              <LayersControl.Overlay name="Boundary" checked>
                <GeoJSON
                  data={boundaryData}
                  style={{
                    color: "#000000",
                    weight: 1,
                    opacity: 0.8,
                    fillOpacity: 0,
                  }}
                />
              </LayersControl.Overlay>
            )}

            {/* Water Drain Lines */}
            {lineData && (
              <LayersControl.Overlay name="Water Drain Lines" checked>
                <GeoJSON
                  data={lineData}
                  style={{
                    color: "#FF5733",
                    weight: 3,
                  }}
                  onEachFeature={bindPopups}
                />
              </LayersControl.Overlay>
            )}

            {/* Water Points */}
            {pointData && (
              <LayersControl.Overlay name="Water Points" checked>
                <GeoJSON
                  data={pointData}
                  pointToLayer={(feature, latlng) =>
                    L.circleMarker(latlng, {
                      radius: 6,
                      fillColor: "#3388FF",
                      color: "#3388FF",
                      weight: 1,
                      opacity: 1,
                      fillOpacity: 0.8,
                    })
                  }
                  onEachFeature={bindPopups}
                />
              </LayersControl.Overlay>
            )}

            {/* Polygons */}
            {polygonData && (
              <LayersControl.Overlay name="Buildings" checked>
                <GeoJSON
                  data={polygonData}
                  style={{
                    color: "#006400",
                    weight: 2,
                    fillOpacity: 0.4,
                  }}
                  onEachFeature={bindPopups}
                />
              </LayersControl.Overlay>
            )}
          </LayersControl>

          {/* Legend */}
          <MapLegendControl
            sections={[{
              shape: "square",
              items: [
                { label: "Water drains", color: "#FF5733" },
                { label: "Water points", color: "#3388FF" },
                { label: "Buildings", color: "#006400" },
              ],
            }]}
          />
        </MapContainer>
      </div>
    </div>
  );
};

export default HydrologicalData;